import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, Result, Button, Alert, Spin, Typography, Space, Descriptions, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, HomeOutlined, MailOutlined } from '@ant-design/icons';
import { supabase } from '../../../api/supabase';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const LunchPaymentReturn = () => {
  const { uniqueLink } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  // Company theme colors
  const theme = {
    primary: '#000000',
    secondary: '#ffffff',
    accent: '#666666',
    lightGrey: '#f5f5f5',
    mediumGrey: '#d9d9d9',
    success: '#52c41a',
    warning: '#faad14',
    error: '#ff4d4f',
    gradient: 'linear-gradient(135deg, #000000 0%, #434343 100%)'
  };

  useEffect(() => {
    console.log('🔄 VoxPay EMAIL/WEB payment return page loaded');
    console.log('URL params:', { uniqueLink });
    console.log('Search params:', Object.fromEntries(searchParams.entries()));
    
    if (uniqueLink) {
      handleVoxPayReturn();
    }
  }, [uniqueLink, searchParams]);

  const handleVoxPayReturn = async () => {
    setLoading(true);
    try {
      // Récupérer les paramètres de retour VoxPay
      const status = searchParams.get('status');
      const transactionId = searchParams.get('transaction_id');
      const amount = searchParams.get('amount');
      const verified = searchParams.get('verified');
      const paymentMethod = searchParams.get('payment_method');

      console.log('💳 VoxPay EMAIL/WEB return params:', {
        status,
        transactionId,
        amount,
        verified,
        paymentMethod,
        uniqueLink
      });

      // Récupérer la réservation
      const { data: reservationData, error: fetchError } = await supabase
        .from('lunch_reservations')
        .select(`
          *,
          lunch_reservation_items (*),
          lunch_signed_contracts (*),
          lunch_payments (*)
        `)
        .eq('unique_link', uniqueLink)
        .single();

      if (fetchError) {
        throw new Error('Réservation non trouvée');
      }

      setReservation(reservationData);

      // Traiter le statut du paiement VoxPay
      if (status === 'success' && verified === 'true') {
        await handleSuccessfulVoxPayPayment(reservationData, transactionId, amount);
        setPaymentStatus('success');
      } else if (status === 'cancelled') {
        await handleCancelledVoxPayPayment(reservationData, transactionId);
        setPaymentStatus('cancelled');
      } else if (status === 'failure') {
        await handleFailedVoxPayPayment(reservationData, transactionId);
        setPaymentStatus('failed');
      } else {
        // Status incertain, vérifier dans la base
        if (reservationData.status === 'paid') {
          setPaymentStatus('success');
        } else {
          setPaymentStatus('pending');
        }
      }

    } catch (error) {
      console.error('❌ Error handling VoxPay EMAIL/WEB return:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulVoxPayPayment = async (reservationData, transactionId, amount) => {
    try {
      console.log('✅ Processing successful VoxPay EMAIL/WEB payment...');

      // Mettre à jour le statut de la réservation si pas déjà fait
      if (reservationData.status !== 'paid') {
        const { error: updateError } = await supabase
          .from('lunch_reservations')
          .update({ 
            status: 'paid',
            payment_date: new Date().toISOString()
          })
          .eq('id', reservationData.id);

        if (updateError) {
          console.error('❌ Error updating reservation status:', updateError);
        }

        // Enregistrer le paiement VoxPay EMAIL/WEB
        const { error: paymentError } = await supabase
          .from('lunch_payments')
          .insert([{
            lunch_reservation_id: reservationData.id,
            amount: parseFloat(amount) || reservationData.total_amount,
            payment_method: 'voxpay_email_web_return',
            transaction_id: transactionId || `voxpay_return_${Date.now()}`,
            status: 'completed',
            created_at: new Date().toISOString(),
            up2pay_response: JSON.stringify({
              status: 'success',
              payment_mode: 'EMAIL_WEB',
              return_url: window.location.href,
              verified: true
            })
          }]);

        if (paymentError) {
          console.error('❌ Error recording VoxPay payment:', paymentError);
        }

        // Envoyer notification à Master Ski Pilou
        await sendVoxPayNotificationToMasterSki(reservationData);
      }

    } catch (error) {
      console.error('❌ Error processing successful VoxPay payment:', error);
    }
  };

  const handleCancelledVoxPayPayment = async (reservationData, transactionId) => {
    try {
      console.log('⚠️ Processing cancelled VoxPay EMAIL/WEB payment...');

      // Enregistrer l'annulation
      const { error: paymentError } = await supabase
        .from('lunch_payments')
        .insert([{
          lunch_reservation_id: reservationData.id,
          amount: reservationData.total_amount,
          payment_method: 'voxpay_email_web_cancelled',
          transaction_id: transactionId || `voxpay_cancel_${Date.now()}`,
          status: 'cancelled',
          created_at: new Date().toISOString(),
          up2pay_response: JSON.stringify({
            status: 'cancelled',
            payment_mode: 'EMAIL_WEB',
            return_url: window.location.href,
            reason: 'User cancelled VoxPay payment'
          })
        }]);

      if (paymentError) {
        console.warn('⚠️ Warning recording VoxPay cancellation:', paymentError);
      }

    } catch (error) {
      console.error('❌ Error processing VoxPay cancellation:', error);
    }
  };

  const handleFailedVoxPayPayment = async (reservationData, transactionId) => {
    try {
      console.log('❌ Processing failed VoxPay EMAIL/WEB payment...');

      // Enregistrer l'échec
      const { error: paymentError } = await supabase
        .from('lunch_payments')
        .insert([{
          lunch_reservation_id: reservationData.id,
          amount: reservationData.total_amount,
          payment_method: 'voxpay_email_web_failed',
          transaction_id: transactionId || `voxpay_fail_${Date.now()}`,
          status: 'failed',
          created_at: new Date().toISOString(),
          up2pay_response: JSON.stringify({
            status: 'failed',
            payment_mode: 'EMAIL_WEB',
            return_url: window.location.href,
            reason: 'VoxPay payment failed'
          })
        }]);

      if (paymentError) {
        console.warn('⚠️ Warning recording VoxPay failure:', paymentError);
      }

    } catch (error) {
      console.error('❌ Error processing VoxPay failure:', error);
    }
  };

  const sendVoxPayNotificationToMasterSki = async (reservationData) => {
    try {
      console.log('📧 Sending VoxPay EMAIL/WEB notification to Master Ski Pilou...');
      
      const emailData = {
        to: 'masterskipilou@example.com',
        subject: `🎉 Paiement VoxPay Confirmé - ${reservationData.client_name} - ${dayjs(reservationData.charter_date).format('DD/MM/YYYY')}`,
        message: `
🏝️ PAIEMENT VOXPAY EMAIL/WEB CONFIRMÉ - MASTER SKI PILOU

✅ CONTRAT SIGNÉ ET PAIEMENT VOXPAY EFFECTUÉ

📋 DÉTAILS CLIENT:
• Nom: ${reservationData.client_name}
• Email: ${reservationData.client_email}
• Téléphone: ${reservationData.client_phone || 'Non renseigné'}
• Date charter: ${dayjs(reservationData.charter_date).format('DD/MM/YYYY')}
• Bateau: ${reservationData.boat_name}
• Type sortie: ${reservationData.trip_type}

🛒 ARTICLES COMMANDÉS:
${reservationData.lunch_reservation_items?.map(item => 
  `• ${item.item_name}: ${item.quantity} x ${item.unit_price}€ = ${item.total_price}€`
).join('\n') || 'Articles non disponibles'}

💰 MONTANT TOTAL: ${reservationData.total_amount}€

✍️ SIGNATURE ET PAIEMENT VOXPAY:
• Contrat signé électroniquement: ${reservationData.lunch_signed_contracts?.[0]?.signature_date ? 
  dayjs(reservationData.lunch_signed_contracts[0].signature_date).format('DD/MM/YYYY HH:mm') : 'Oui'}
• Paiement VoxPay EMAIL/WEB confirmé: ${dayjs().format('DD/MM/YYYY HH:mm')}
• Mode de paiement: Email/Web (pas d'appel téléphonique)

🔗 LIEN RÉSERVATION CRM:
${window.location.origin}/admin/lunch-reservations

📧 Veuillez valider cette réservation dans le CRM pour envoyer l'email de confirmation au client.

---
Système Master Ski Pilou - VoxPay EMAIL/WEB
${dayjs().format('DD/MM/YYYY HH:mm')}
        `,
        reservation_id: reservationData.id,
        client_email: reservationData.client_email,
        total_amount: reservationData.total_amount,
        payment_method: 'voxpay_email_web',
        status: 'payment_confirmed'
      };

      // Webhook vers Make
      const webhookUrl = process.env.REACT_APP_VOXPAY_WEBHOOK;
      if (webhookUrl) {
        await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailData)
        });
        console.log('✅ VoxPay EMAIL/WEB notification sent to Master Ski Pilou');
      } else {
        console.warn('⚠️ VoxPay webhook URL not configured');
      }

    } catch (error) {
      console.error('❌ Error sending VoxPay notification:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: theme.gradient
      }}>
        <Card style={{ textAlign: 'center', maxWidth: 400, border: `2px solid ${theme.mediumGrey}` }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Vérification de votre paiement VoxPay...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: theme.gradient
      }}>
        <Card style={{ maxWidth: 500, border: `2px solid ${theme.error}` }}>
          <Result
            status="error"
            title="Erreur VoxPay"
            subTitle={error}
            extra={
              <Button 
                type="primary" 
                icon={<HomeOutlined />}
                onClick={() => window.location.href = '/'}
                style={{ 
                  backgroundColor: theme.primary, 
                  borderColor: theme.primary 
                }}
              >
                Retour à l'accueil
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: theme.gradient,
      padding: '24px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <Card style={{ 
          marginBottom: '24px', 
          textAlign: 'center',
          border: `2px solid ${theme.mediumGrey}`,
          background: theme.secondary
        }}>
          <Title level={2} style={{ margin: '0 0 8px 0', color: theme.primary }}>
            🏝️ Master Ski Pilou
          </Title>
          <Title level={3} style={{ margin: '0 0 16px 0', color: theme.accent }}>
            Retour Paiement VoxPay
          </Title>
        </Card>

        {/* Résultat du paiement VoxPay */}
        <Card style={{ 
          border: `2px solid ${theme.mediumGrey}`,
          background: theme.secondary
        }}>
          {paymentStatus === 'success' && (
            <Result
              status="success"
              title={<span style={{ color: theme.success }}>Paiement VoxPay Réussi ! 🎉</span>}
              subTitle={
                <div>
                  <Paragraph style={{ fontSize: '16px', color: theme.accent }}>
                    Merci <strong style={{ color: theme.primary }}>{reservation?.client_name}</strong> ! 
                    Votre paiement VoxPay de <strong style={{ color: theme.success }}>{reservation?.total_amount}€</strong> a été traité avec succès.
                  </Paragraph>
                  
                  <Alert
                    message="✅ Paiement VoxPay confirmé"
                    description={
                      <div style={{ textAlign: 'left' }}>
                        <p>💳 Paiement traité par VoxPay (Email/Web)</p>
                        <p>✍️ Contrat signé électroniquement</p>
                        <p>📧 Master Ski Pilou a été automatiquement notifié</p>
                        <p>⏳ Vous recevrez un email de confirmation après validation</p>
                      </div>
                    }
                    type="success"
                    showIcon
                    style={{ margin: '24px 0' }}
                  />

                  {reservation && (
                    <Descriptions 
                      column={1} 
                      bordered 
                      size="small"
                      style={{ marginTop: '24px', textAlign: 'left' }}
                    >
                      <Descriptions.Item label="Date du charter">
                        {dayjs(reservation.charter_date).format('DD/MM/YYYY')}
                      </Descriptions.Item>
                      <Descriptions.Item label="Bateau">
                        {reservation.boat_name}
                      </Descriptions.Item>
                      <Descriptions.Item label="Type de sortie">
                        {reservation.trip_type}
                      </Descriptions.Item>
                      <Descriptions.Item label="Montant payé">
                        <Tag color="success" style={{ fontSize: '14px' }}>
                          💳 VoxPay: {reservation.total_amount}€
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Mode de paiement">
                        <Tag color="purple">📧 VoxPay Email/Web</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Date de paiement">
                        {dayjs().format('DD/MM/YYYY HH:mm')}
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </div>
              }
              extra={[
                <Space key="actions" direction="vertical" style={{ width: '100%' }}>
                  <Button 
                    type="primary" 
                    size="large"
                    icon={<HomeOutlined />}
                    onClick={() => window.location.href = '/'}
                    style={{ 
                      backgroundColor: theme.primary, 
                      borderColor: theme.primary,
                      minWidth: '200px'
                    }}
                  >
                    Retour à l'accueil
                  </Button>
                  
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Un email de confirmation vous sera envoyé après validation par Master Ski Pilou
                  </Text>
                </Space>
              ]}
            />
          )}

          {paymentStatus === 'cancelled' && (
            <Result
              status="warning"
              title={<span style={{ color: theme.warning }}>Paiement VoxPay Annulé</span>}
              subTitle={
                <div>
                  <Paragraph style={{ color: theme.accent }}>
                    Vous avez annulé le paiement VoxPay. Votre réservation est toujours en attente.
                  </Paragraph>
                  
                  <Alert
                    message="⚠️ Paiement VoxPay requis"
                    description="Votre contrat a été signé mais le paiement VoxPay n'a pas été effectué. Vous pouvez reprendre le paiement à tout moment."
                    type="warning"
                    showIcon
                    style={{ margin: '24px 0' }}
                  />
                </div>
              }
              extra={[
                <Space key="actions">
                  <Button 
                    type="primary"
                    icon={<MailOutlined />}
                    onClick={() => window.location.href = `/lunch-reservation/${uniqueLink}`}
                    style={{ 
                      backgroundColor: theme.primary, 
                      borderColor: theme.primary 
                    }}
                  >
                    Reprendre le paiement VoxPay
                  </Button>
                  <Button 
                    icon={<HomeOutlined />}
                    onClick={() => window.location.href = '/'}
                  >
                    Retour à l'accueil
                  </Button>
                </Space>
              ]}
            />
          )}

          {paymentStatus === 'failed' && (
            <Result
              status="error"
              title={<span style={{ color: theme.error }}>Paiement VoxPay Échoué</span>}
              subTitle={
                <div>
                  <Paragraph style={{ color: theme.accent }}>
                    Une erreur est survenue lors du traitement de votre paiement VoxPay.
                  </Paragraph>
                  
                  <Alert
                    message="❌ Échec du paiement VoxPay"
                    description="Le paiement VoxPay n'a pas pu être traité. Veuillez réessayer ou contacter Master Ski Pilou."
                    type="error"
                    showIcon
                    style={{ margin: '24px 0' }}
                  />
                </div>
              }
              extra={[
                <Space key="actions">
                  <Button 
                    type="primary"
                    icon={<MailOutlined />}
                    onClick={() => window.location.href = `/lunch-reservation/${uniqueLink}`}
                    style={{ 
                      backgroundColor: theme.primary, 
                      borderColor: theme.primary 
                    }}
                  >
                    Réessayer VoxPay
                  </Button>
                  <Button 
                    icon={<HomeOutlined />}
                    onClick={() => window.location.href = '/'}
                  >
                    Retour à l'accueil
                  </Button>
                </Space>
              ]}
            />
          )}

          {paymentStatus === 'pending' && (
            <Result
              status="info"
              title={<span style={{ color: theme.primary }}>Statut VoxPay En Attente</span>}
              subTitle={
                <div>
                  <Paragraph style={{ color: theme.accent }}>
                    Le statut de votre paiement VoxPay est en cours de vérification.
                  </Paragraph>
                  
                  <Alert
                    message="ℹ️ Vérification VoxPay en cours"
                    description="Si vous avez effectué le paiement VoxPay, il sera traité sous peu. Sinon, vous pouvez reprendre le processus."
                    type="info"
                    showIcon
                    style={{ margin: '24px 0' }}
                  />
                </div>
              }
              extra={[
                <Space key="actions">
                  <Button 
                    type="primary"
                    icon={<MailOutlined />}
                    onClick={() => window.location.href = `/lunch-reservation/${uniqueLink}`}
                    style={{ 
                      backgroundColor: theme.primary, 
                      borderColor: theme.primary 
                    }}
                  >
                    Effectuer le paiement VoxPay
                  </Button>
                  <Button 
                    icon={<HomeOutlined />}
                    onClick={() => window.location.href = '/'}
                  >
                    Retour à l'accueil
                  </Button>
                </Space>
              ]}
            />
          )}
        </Card>

        {/* Informations VoxPay */}
        <Card style={{ 
          marginTop: '24px',
          textAlign: 'center',
          border: `1px solid ${theme.lightGrey}`,
          background: theme.secondary
        }}>
          <Title level={4} style={{ color: theme.primary }}>💳 À propos de VoxPay</Title>
          <Paragraph style={{ color: theme.accent }}>
            VoxPay est une plateforme de paiement française sécurisée.<br/>
            Votre paiement a été traité en mode Email/Web (sans appel téléphonique).
          </Paragraph>
          <Space direction="vertical">
            <Text><strong>🔒 Sécurité :</strong> Paiement certifié PCI-DSS</Text>
            <Text><strong>📧 Mode :</strong> Email/Web uniquement</Text>
            <Text><strong>🇫🇷 Support :</strong> Entreprise française</Text>
          </Space>
        </Card>

        {/* Contact */}
        <Card style={{ 
          marginTop: '24px',
          textAlign: 'center',
          border: `1px solid ${theme.lightGrey}`,
          background: theme.secondary
        }}>
          <Title level={4} style={{ color: theme.primary }}>Besoin d'aide ?</Title>
          <Paragraph style={{ color: theme.accent }}>
            Si vous avez des questions concernant votre réservation ou votre paiement VoxPay,<br/>
            n'hésitez pas à contacter Master Ski Pilou :
          </Paragraph>
          <Space direction="vertical">
            <Text><strong>Téléphone :</strong> 05 90 27 91 79</Text>
            <Text><strong>Email :</strong> masterskipilou@example.com</Text>
            <Text><strong>Adresse :</strong> Rue Jeanne d'Arc, GUSTAVIA - 97133 Saint-Barthélemy</Text>
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default LunchPaymentReturn;